// odds-api-client.ts

export interface OddsApiClientOptions {
  api_key: string;
  base_url?: string; // default: https://api2.odds-api.io/v3
  default_sport?: string; // e.g. "football", "basketball"
  default_bookmakers?: string[]; // e.g. ["Bet365", "Pinnacle"]
}

// ---- API types ----

export interface OddsApiLeagueRef {
  name: string;
  slug: string;
}

export interface OddsApiSportRef {
  name: string;
  slug: string;
}

export interface OddsApiEventScores {
  home: number | null;
  away: number | null;
}

export interface OddsApiEventSummary {
  id: number;
  date: string; // RFC3339
  home: string;
  away: string;
  homeId: number;
  awayId: number;
  league: OddsApiLeagueRef;
  sport: OddsApiSportRef;
  status: string; // "pending" | "live" | "settled" | etc.
  scores?: OddsApiEventScores | null;
}

export interface OddsApiOddsRow {
  label?: string; // player name, market label etc.
  hdp?: number;

  home?: string;
  away?: string;
  draw?: string;
  over?: string;
  under?: string;
  yes?: string;
  no?: string;

  homeLink?: string;
  awayLink?: string;
  drawLink?: string;

  // exchanges / depth (strings per docs)
  max?: number;
  depthHome?: string;
  depthAway?: string;
  depthDraw?: string;
  depthOver?: string;
  depthUnder?: string;
  depthLayHome?: string;
  depthLayAway?: string;
  depthLayDraw?: string;
  depthLayOver?: string;
  depthLayUnder?: string;
  layHome?: string;
  layAway?: string;
  layDraw?: string;
  layOver?: string;
  layUnder?: string;
  layYes?: string;
  layNo?: string;
}

export interface OddsApiBookmakerOdds {
  name: string;
  odds: OddsApiOddsRow[];
  updatedAt: string;
}

export interface OddsApiEventOdds {
  id: number;
  date: string;
  home: string;
  away: string;
  status: string;
  league: OddsApiLeagueRef;
  sport: OddsApiSportRef;
  bookmakers: Record<string, OddsApiBookmakerOdds[]>;
  urls?: Record<string, string>;
}

export interface OddsApiParticipant {
  id: number;
  name: string;
  sport: string; // sport slug
}

// ---- Client ----

export class OddsApiClient {
  private api_key: string;
  private base_url: string;
  private default_sport?: string;
  private default_bookmakers?: string[];

  constructor(options: OddsApiClientOptions) {
    const { api_key, base_url, default_sport, default_bookmakers } = options;

    this.api_key = api_key;
    this.base_url = base_url ?? "https://api2.odds-api.io/v3";
    this.default_sport = default_sport;
    this.default_bookmakers = default_bookmakers;
  }

  private buildUrl(path: string, query: Record<string, string | number | undefined>): string {
    const url = new URL(this.base_url + path);

    url.searchParams.set("apiKey", this.api_key);

    Object.entries(query).forEach(([key, value]) => {
      if (value === undefined || value === null) return;
      url.searchParams.set(key, String(value));
    });

    return url.toString();
  }

  private async request<T>(path: string, query: Record<string, string | number | undefined>): Promise<T> {
    const url = this.buildUrl(path, query);
    const res = await fetch(url);

    if (!res.ok) {
      const text = await res.text().catch(() => "");
      throw new Error(`OddsApiClient error ${res.status}: ${text || res.statusText}`);
    }

    return (await res.json()) as T;
  }

  // GET /v3/sports (no auth required in docs, but key is fine)
  async getSports(): Promise<{ name: string; slug: string }[]> {
    return this.request("/sports", {});
  }

  // GET /v3/leagues
  async getLeagues(params: { sport?: string } = {}): Promise<{ name: string; slug: string; eventsCount: number }[]> {
    const { sport } = params;

    return this.request("/leagues", {
      sport: sport ?? this.default_sport,
    });
  }

  // GET /v3/events
  async getEvents(params: {
    sport: string;
    league?: string;
    participant_id?: number;
    status?: string; // "pending,live,settled"
    from?: string; // RFC3339
    to?: string; // RFC3339
    bookmaker?: string;
  }): Promise<OddsApiEventSummary[]> {
    const {
      sport,
      league,
      participant_id,
      status,
      from,
      to,
      bookmaker,
    } = params;

    return this.request<OddsApiEventSummary[]>("/events", {
      sport,
      league,
      participantId: participant_id,
      status,
      from,
      to,
      bookmaker,
    });
  }

  // GET /v3/events (live only via separate endpoint) – optional helper
  async getLiveEvents(params: {
    sport?: string;
    bookmaker?: string;
  } = {}): Promise<OddsApiEventSummary[]> {
    const { sport, bookmaker } = params;

    return this.request<OddsApiEventSummary[]>("/events/live", {
      sport: sport ?? this.default_sport,
      bookmaker,
    });
  }

  // GET /v3/events/{id}
  async getEventById(event_id: number | string): Promise<OddsApiEventSummary> {
    return this.request<OddsApiEventSummary>(`/events/${event_id}`, {});
  }

  // GET /v3/events/search
  async searchEvents(params: {
    query: string;
    sport?: string;
    league?: string;
  }): Promise<OddsApiEventSummary[]> {
    const { query, sport, league } = params;

    return this.request<OddsApiEventSummary[]>("/events/search", {
      q: query,
      sport: sport ?? this.default_sport,
      league,
    });
  }

  // GET /v3/odds – single event odds (including player props)
  async getEventOdds(params: {
    event_id: number | string;
    bookmakers?: string[] | string;
  }): Promise<OddsApiEventOdds> {
    const { event_id } = params;
    const bookmakers =
      typeof params.bookmakers === "string"
        ? params.bookmakers
        : (params.bookmakers ?? this.default_bookmakers)?.join(",");

    if (!bookmakers) {
      throw new Error("bookmakers are required (pass in call or set default_bookmakers)");
    }

    return this.request<OddsApiEventOdds>("/odds", {
      eventId: String(event_id),
      bookmakers,
    });
  }

  // GET /v3/odds/multi – multiple events in one call (light wrapper)
  async getOddsForMultipleEvents(params: {
    event_ids: (number | string)[];
    bookmakers?: string[] | string;
  }): Promise<OddsApiEventOdds[]> {
    const { event_ids } = params;

    if (!event_ids.length) {
      return [];
    }

    const bookmakers =
      typeof params.bookmakers === "string"
        ? params.bookmakers
        : (params.bookmakers ?? this.default_bookmakers)?.join(",");

    if (!bookmakers) {
      throw new Error("bookmakers are required (pass in call or set default_bookmakers)");
    }

    return this.request<OddsApiEventOdds[]>("/odds/multi", {
      eventIds: event_ids.join(","),
      bookmakers,
    });
  }

  // GET /v3/participants
  async getParticipants(params: {
    sport: string;
    search?: string;
  }): Promise<OddsApiParticipant[]> {
    const { sport, search } = params;

    return this.request<OddsApiParticipant[]>("/participants", {
      sport,
      search,
    });
  }

  // GET /v3/participants/{id}
  async getParticipantById(participant_id: number | string): Promise<OddsApiParticipant> {
    return this.request<OddsApiParticipant>(`/participants/${participant_id}`, {});
  }
}


export function createOddsApiClient(options: OddsApiClientOptions): OddsApiClient {
	return new OddsApiClient(options);
}
